import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Car, Shield } from 'lucide-react';
import CoverageModelSelector from '../shared/CoverageModelSelector';

function VehicleInsuranceForm({ item, vehicles, contracts, onSave, onCancel }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    vehicle_id: item?.vehicle_id || '',
    contract_id: item?.contract_id || '',
    assigned_at: item?.assigned_at || new Date().toISOString().split('T')[0],
    is_primary: item?.is_primary ?? true,
    premium_amount: item?.premium_amount || '',
    coverage_model_id: item?.coverage_model_id || null,
    valeur_assuree: item?.valeur_assuree || null,
    notes: item?.notes || '',
  });

  // Coverage model selection state
  const [coverageModelValue, setCoverageModelValue] = useState({
    coverageModelId: item?.coverage_model_id || null,
    overrides: {},
  });

  const handleCoverageModelChange = useCallback((val) => {
    setCoverageModelValue(val);
    setFormData(prev => ({ ...prev, coverage_model_id: val.coverageModelId }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex justify-between items-center sticky top-0 z-10">
          <h3 className="text-2xl font-bold text-white">
            {item ? t('insuranceConfig.vehicleAssignmentForm.titleEdit') : t('insuranceConfig.vehicleAssignmentForm.titleCreate')}
          </h3>
          <button onClick={onCancel} className="text-white hover:bg-blue-700 p-2 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Car className="w-5 h-5" />
              {t('insuranceConfig.vehicleAssignmentForm.generalSection')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.vehicleAssignmentForm.vehicle')}
                </label>
                <select
                  value={formData.vehicle_id}
                  onChange={(e) => handleChange('vehicle_id', parseInt(e.target.value))}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">{t('insuranceConfig.vehicleAssignmentForm.vehiclePlaceholder')}</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.license_plate} - {v.make} {v.model}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.vehicleAssignmentForm.contract')}
                </label>
                <select
                  value={formData.contract_id}
                  onChange={(e) => handleChange('contract_id', parseInt(e.target.value))}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">{t('insuranceConfig.vehicleAssignmentForm.contractPlaceholder')}</option>
                  {contracts.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.policy_number} - {c.insurer_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.vehicleAssignmentForm.assignedAt')}
                </label>
                <input
                  type="date"
                  value={formData.assigned_at}
                  onChange={(e) => handleChange('assigned_at', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.vehicleAssignmentForm.insuredValue')}
                </label>
                <input
                  type="number"
                  value={formData.valeur_assuree || ''}
                  onChange={(e) => handleChange('valeur_assuree', e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder={t('insuranceConfig.vehicleAssignmentForm.insuredValuePlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Coverage Model Selection (replaces 17 legacy boolean fields) */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {t('insuranceConfig.vehicleAssignmentForm.coverageSection')}
            </h4>
            <CoverageModelSelector
              value={coverageModelValue}
              onChange={handleCoverageModelChange}
            />
          </div>

          {/* Premium */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-4">{t('insuranceConfig.vehicleAssignmentForm.premiumSection')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.vehicleAssignmentForm.annualPremium')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.premium_amount || ''}
                  onChange={(e) => handleChange('premium_amount', e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceConfig.vehicleAssignmentForm.insuredValueEur')}
                </label>
                <input
                  type="number"
                  value={formData.valeur_assuree || ''}
                  onChange={(e) => handleChange('valeur_assuree', e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="25000"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('insuranceConfig.vehicleAssignmentForm.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder={t('insuranceConfig.vehicleAssignmentForm.notesPlaceholder')}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {item ? t('insuranceConfig.vehicleAssignmentForm.submitUpdate') : t('insuranceConfig.vehicleAssignmentForm.submitCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VehicleInsuranceForm;
